import React from "react";
import { motion } from "framer-motion";
import { SectionLabel } from "./Bits";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";

/**
 * Editorial sub-page hero: cinematic image band, eyebrow, large title lines,
 * optional tagline + intro paragraph.
 */
export default function PageHero({
  eyebrow,
  titleLines = [],
  tagline,
  intro,
  image,
  align = "left",
  compact = false,
  children,
}) {
  const reduced = usePrefersReducedMotion();

  return (
    <section
      data-testid="page-hero"
      className="relative isolate flex items-end overflow-hidden bg-nearblack"
      style={{ minHeight: compact ? "clamp(420px, 54vh, 560px)" : "clamp(520px, 74vh, 760px)" }}
    >
      {image && (
        <div className="absolute inset-0 z-0">
          <img src={image} alt="" className="h-full w-full object-cover" loading="eager" />
        </div>
      )}
      <div
        className="grain absolute inset-0 z-10"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(14,13,20,0.78) 0%, rgba(14,13,20,0.6) 45%, rgba(14,13,20,0.9) 100%)",
        }}
      />

      <div className="relative z-20 w-full pb-[clamp(34px,3.6vw,56px)] pt-[clamp(120px,13vh,158px)]">
        <div className="lk-container-wide">
          <div className={align === "center" ? "mx-auto max-w-[900px] text-center" : "max-w-[860px]"}>
            {eyebrow && (
              <motion.div
                initial={{ opacity: 0, y: reduced ? 0 : 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className={align === "center" ? "flex justify-center" : ""}
              >
                <SectionLabel onDark>{eyebrow}</SectionLabel>
              </motion.div>
            )}

            <h1 className="display-xl mt-6" style={{ color: "#FBFAF7" }} data-testid="page-hero-title">
              {titleLines.map((line, i) => (
                <motion.span
                  key={line}
                  className="block"
                  initial={{ opacity: 0, y: reduced ? 0 : 22 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: reduced ? 0 : 0.08 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              ))}
            </h1>

            {tagline && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: reduced ? 0 : 0.35 }}
                className="mt-7 font-display text-[clamp(16px,1.5vw,20px)] font-medium italic"
                style={{ color: "rgba(216,183,122,0.95)", letterSpacing: "-0.01em" }}
              >
                {tagline}
              </motion.p>
            )}

            {intro && (
              <motion.p
                initial={{ opacity: 0, y: reduced ? 0 : 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: reduced ? 0 : 0.42 }}
                className={`mt-6 text-[15px] leading-[1.68] sm:text-[16px] ${align === "center" ? "mx-auto" : ""}`}
                style={{ color: "rgba(247,246,242,0.75)", maxWidth: "58ch" }}
              >
                {intro}
              </motion.p>
            )}

            {children && <div className="mt-10">{children}</div>}
          </div>
        </div>
      </div>
    </section>
  );
}
